<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { getAcceptString, validateArtworkFileType } from "common/artwork-file-types";
import { trackUploadInitiated } from "../utils/tracker";
import { MODES } from "../misc/constants";

const props = defineProps({
  sideKey: {
    type: String,
    required: true
  }
});

const store = useStore();
const acceptedFileTypes = getAcceptString();

function uploadArtwork(file: File) {
  if (!store.getters["app/supportsMade"]) {
    window.alert("This product color is not supported in Jiffy Made.");
    return;
  }

  const validation = validateArtworkFileType(file);
  if ((validation as any).valid) {
    trackUploadInitiated(props.sideKey, store.getters["app/mode"], file);
    store.commit("app/setMode", MODES.MADE);
    store.dispatch(`artwork_${props.sideKey}/upload`, file);
  }
}

const inputId = computed(() => `field-artwork-upload-${props.sideKey}`);
const supportsMade = computed(() => store.getters["app/supportsMade"]);
</script>

<template>
  <div class="artwork-upload" data-testid="artwork-upload">
    <UseSvg class="artwork-upload__icon" icon="upload-cloud" type="print" />
    <label class="artwork-upload__label" :for="inputId">Start Design Upload</label>
    <input
      :id="inputId"
      :accept="acceptedFileTypes"
      class="artwork-upload__input"
      :class="{ 'artwork-upload__input--disabled': !supportsMade }"
      type="file"
      :disabled="!supportsMade"
      @change="uploadArtwork(($event.target as HTMLInputElement).files![0]!)"
    />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-upload {
  $this: &;

  display: flex;
  pointer-events: all;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  border: 4px solid $pod-upload-border;
  border-radius: 12px;
  max-width: 320px;
  height: 52px;
  margin: 20px auto;
  color: $white;
  background-color: $pod-upload-bg;
  box-shadow: 0 0 12px $pod-upload-bg;
  transition:
    box-shadow 0.3s,
    background-color 0.3s,
    border-color 0.3s;
  cursor: pointer;

  @include with-container(container, $pod-pdp-desktop-response) {
    gap: 6px;
    height: 46px;
  }

  &:focus,
  &:hover {
    box-shadow: 0 0 12px 3px $pod-upload-bg;
  }

  &__icon {
    width: 34px;
    height: 34px;

    @include with-container(container, $pod-pdp-desktop-response) {
      width: 30px;
      height: 30px;
    }
  }

  &__label {
    display: block;
    margin-bottom: 4px;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.2;
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.25);

    @include with-container(container, $pod-pdp-desktop-response) {
      font-size: 16px;
      margin-bottom: 0;
    }
  }

  &__input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }
}
</style>
